import React, { useState, useEffect } from 'react';
import { IntegrationField } from '../types';
import { getJiraProjects } from '../../../api';
import HelpIcon from './HelpIcon';

interface JiraProjectSelectorComponentProps {
  field: IntegrationField;
  value: string;
  error?: string;
  integrationTypeId: number;
  customerIntegrationId: number | null;
  onChange: (name: string, value: string) => void;
  configValues: Record<string, any>;
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
  projectTypeKey: string;
  description?: string;
}

const JiraProjectSelectorComponent: React.FC<JiraProjectSelectorComponentProps> = ({
  field,
  value,
  error,
  integrationTypeId,
  customerIntegrationId,
  onChange,
  configValues
}) => {
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const commonLabelStyles = {
    display: 'block',
    marginBottom: 8,
    fontWeight: 500 as const
  };

  const commonSelectStyles = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 4,
    border: `1px solid ${error ? '#f44336' : '#ccc'}`,
    fontSize: 14,
    boxSizing: 'border-box' as const
  };

  const errorMessageStyles = {
    color: '#f44336',
    fontSize: 12,
    marginTop: 4
  };

  const fetchJiraProjects = async () => {
    if (!customerIntegrationId && (!configValues.jiraUrl || !configValues.email || !configValues.apiToken)) {
      // Don't try to fetch projects if we don't have the necessary config values
      setLoadError('Please enter valid details for Jira URL, Email, and API Token first');
      return;
    }

    if (jiraProjects.length > 0) {
      setLoading(false);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      // If we have a customerIntegrationId, use that to fetch projects
      // Otherwise, use the current config values for validation
      const { projects } = await getJiraProjects(
        integrationTypeId,
        configValues.jiraUrl,
        configValues.email,
        configValues.apiToken
      );

      setJiraProjects(projects);
    } catch (err) {
      console.error('Failed to load Jira projects:', err);
      setLoadError('Failed to load Jira projects. Please check you have entered valid credentials above');
    } finally {
      setLoading(false);
    }
  };

  const getProjectDisplayName = (projectKey: string) => {
    const project = jiraProjects.find(p => p.key === projectKey);
    return project ? `${project.name}` : configValues.projectName || projectKey;
  };

  // Render the project selector
  const renderProjectSelector = () => {
    // If we have a value and not editing, show as label with change button
    if (value && !isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            ...commonSelectStyles,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f5f5f5',
            flex: 1
          }}>
            {getProjectDisplayName(value)}
          </div>
          <button
            onClick={() => {
              setIsEditing(true);
              fetchJiraProjects();
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Change
          </button>
        </div>
      );
    }

    // If we have no projects and we're not loading, show a button to fetch them
    if (jiraProjects.length === 0 && !loading && !value) {
      return (
        <div>
          <button
            onClick={fetchJiraProjects}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left'
            }}
          >
            Click to load Jira projects
          </button>
          {loadError && <div style={{ marginTop: 8, fontSize: 12, color: '#f44336' }}>{loadError}</div>}
        </div>
      );
    }

    // Otherwise show the dropdown
    return (
      <div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: loading || loadError ? '8px' : '0' }}>
          <select
            id={field.name}
            name={field.name}
            value={value}
            onChange={(e) => {
              const selectedKey = e.target.value;
              const selectedProject = jiraProjects.find(p => p.key === selectedKey);
              
              onChange(field.name, selectedKey); // Store the key
              if (selectedProject) {
                onChange('projectName', selectedProject.name); // Store the name
              }
              setIsEditing(false);
            }}
            required={field.required}
            style={{ ...commonSelectStyles, flex: 1 }}
            disabled={loading}
          >
            <option value="">Select a project</option>
            {jiraProjects.map((project) => (
              <option key={project.key} value={project.key}>
                {project.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setIsEditing(false);
              if (value) {
                // If we had a previous value, restore it
                onChange(field.name, value);
              }
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
        {loading && <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>Loading Jira projects...</div>}
        {loadError && <div style={{ marginTop: 8, fontSize: 12, color: '#f44336' }}>{loadError}</div>}
        {jiraProjects.length === 0 && !loading && !loadError && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#ff9800' }}>
            No projects found. Please check your permissions.
          </div>
        )}
      </div>
    );
  };

  // Render the field with common wrapper
  return (
    <div key={field.name} style={{ marginBottom: 16 }}>
      <label htmlFor={field.name} style={{ ...commonLabelStyles, display: 'flex', alignItems: 'center' }}>
        <span>
          {field.label}{field.required && <span style={{ color: 'red' }}> *</span>}
        </span>
        {field.helpText && <HelpIcon helpText={field.helpText} fieldLabel={field.label} />}
      </label>
      {renderProjectSelector()}
      {error && <div style={errorMessageStyles}>{error}</div>}
    </div>
  );
};

export default JiraProjectSelectorComponent;
